import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowDown, ArrowUpRight, Megaphone, Bell, MessageSquare,
  Layout, Zap, Clock, Sparkles, MousePointerClick, Target, Split,
  Filter, Flame, TrendingUp, Eye, ShieldCheck, Layers,
  CheckCircle2, Check, HelpCircle, XCircle, AlertTriangle, Search, Gauge,
  ShoppingCart, Tag, X, Settings, Palette, Type, SlidersHorizontal, PanelTop, Maximize2, Users
} from 'lucide-react';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import SimpleKakaoButton from '../../Components/SimpleKakaoButton';

const ServicePopup = () => {
  const [scrolled, setScrolled] = useState(false);

  // 인터랙티브: 팝업 형태 전환
  const [popupType, setPopupType] = useState('modal'); // 'modal' | 'bar' | 'slide' | 'full'

  // 카운트다운 배너용 타이머
  const [countdown, setCountdown] = useState({ h: 5, m: 59, s: 59 });
  const pad = (n) => String(n).padStart(2, '0');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setCountdown((prev) => {
        let { h, m, s } = prev;
        s -= 1;
        if (s < 0) { s = 59; m -= 1; }
        if (m < 0) { m = 59; h -= 1; }
        if (h < 0) { h = 0; m = 0; s = 0; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // 활용 사례
  const useCases = [
    {
      emoji: <ShoppingCart className="w-5 h-5" />,
      title: '장바구니 이탈 방지',
      type: '이커머스',
      problem: '결제 직전 이탈하는 고객이 장바구니 진입 대비 68%에 달함',
      solution: '이탈 의도 감지 시점에 "지금 구매 시 5% 추가 할인" 모달 팝업 노출',
      result: '장바구니 전환율 +27%',
      color: 'teal',
      glow: 'shadow-teal-500/20'
    },
    {
      emoji: <Bell className="w-5 h-5" />,
      title: '뉴스레터 구독 확대',
      type: '미디어 / 콘텐츠',
      problem: '콘텐츠는 잘 읽히지만 구독 전환율이 0.5%에 불과',
      solution: '스크롤 60% 도달 시점에 맞춰 슬라이드인 구독 배너를 노출',
      result: '구독 전환 3.4배',
      color: 'blue',
      glow: 'shadow-blue-500/20'
    },
    {
      emoji: <Tag className="w-5 h-5" />,
      title: '시즌 프로모션 강조',
      type: '리테일',
      problem: '할인 이벤트 인지율이 낮아 참여가 저조하고 매출로 이어지지 않음',
      solution: '상단 고정 띠배너에 실시간 카운트다운을 더해 마감 임박을 강조',
      result: '이벤트 매출 +52%',
      color: 'rose',
      glow: 'shadow-rose-500/20'
    },
    {
      emoji: <Megaphone className="w-5 h-5" />,
      title: '공지 · 안내 전달',
      type: '서비스',
      problem: '배송 지연·점검 공지를 놓친 고객의 CS 문의가 폭증',
      solution: '전 페이지 상단 띠배너로 안내, 빈도 캡으로 피로도까지 관리',
      result: '관련 CS 문의 -40%',
      color: 'purple',
      glow: 'shadow-purple-500/20'
    }
  ];

  return (
    <div className="bg-white">
      {/* 플로팅 CTA 버튼 */}
      <div className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <Link to="/freestart">
          <button className="flex items-center bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group">
            <span className="font-medium">무료로 시작하기</span>
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </Link>
      </div>

      <Header />

      {/* ==========================================
          섹션 1: Hero Section
      ========================================== */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#0B0F19]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px]"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-600/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal-600/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left: 텍스트 */}
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-semibold mb-8 animate-fade-in-up">
                <Megaphone className="w-4 h-4 fill-emerald-500" />
                <span>Popup &amp; Banner Campaigns</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                떠나려는 고객에게 <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400">건네는 마지막 한 마디</span>
              </h1>

              <p className="text-base sm:text-xl text-gray-400 mb-10 leading-relaxed max-w-lg">
                아무 팝업이나 띄우면 오히려 방문자가 떠납니다.
                적절한 대상에게, 적절한 순간에, 적절한 빈도로 노출하는 <br className="sm:hidden" />데이터 기반 팝업 CRO를 시작하세요.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/freestart">
                  <button className="px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-emerald-900/20 hover:shadow-emerald-500/30 transition-all hover:-translate-y-1">
                    무료로 팝업 만들기
                  </button>
                </Link>
                <Link to="/service/ab-test">
                  <button className="px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-xl font-medium text-lg transition-all backdrop-blur-sm flex items-center justify-center gap-2 group">
                    A/B 테스트 기능 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </div>

              {/* 하단 신뢰 문구 */}
              <div className="mt-8 flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1 text-emerald-400">
                  <Gauge className="w-4 h-4" />
                  <span className="font-bold">Smart Frequency Cap</span>
                </div>
                <p>노출 피로도 없이 전환만 극대화</p>
              </div>
            </div>

            {/* Right: 3D 팝업 비주얼 */}
            <div className="relative hidden lg:block" style={{ perspective: '1500px' }}>
              <div
                className="relative transition-transform duration-700"
                style={{ transform: 'rotateY(-12deg) rotateX(5deg)' }}
              >
                <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden min-h-[500px] relative">

                  {/* Browser Header */}
                  <div className="bg-[#0F131C] px-4 py-3 flex items-center gap-2 border-b border-gray-800">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                    </div>
                    <div className="ml-4 bg-[#1A1F2E] px-3 py-1 rounded text-[10px] text-gray-500 font-mono flex-1 text-center">
                      switchup.io/demo-shop
                    </div>
                  </div>

                  {/* 상단 띠배너 (카운트다운) */}
                  <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-2 flex items-center justify-center gap-2 text-[11px] font-bold relative">
                    <Sparkles className="w-3 h-3" />
                    <span>여름 시즌 최대 40% 할인 · 자정 마감</span>
                    <span className="ml-2 font-mono bg-black/20 px-1.5 rounded">
                      {pad(countdown.h)}:{pad(countdown.m)}:{pad(countdown.s)}
                    </span>
                    <X className="w-3 h-3 absolute right-3 opacity-60" />
                  </div>

                  {/* Webpage Content (Skeleton, dimmed) */}
                  <div className="p-6 opacity-30 grayscale">
                    <div className="flex justify-between items-center mb-8">
                      <div className="w-24 h-6 bg-gray-600 rounded"></div>
                      <div className="flex gap-4">
                        <div className="w-16 h-4 bg-gray-600 rounded"></div>
                        <div className="w-16 h-4 bg-gray-600 rounded"></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-32 bg-gray-700 rounded-lg"></div>
                      ))}
                    </div>
                  </div>

                  {/* Dim Overlay */}
                  <div className="absolute inset-0 bg-black/50"></div>

                  {/* 중앙 모달 팝업 */}
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <div className="w-72 bg-white rounded-2xl shadow-2xl overflow-hidden relative">
                      <div className="h-24 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                        <span className="text-5xl">🎁</span>
                      </div>
                      <button className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white">
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="p-5 text-center">
                        <div className="text-lg font-bold text-gray-900 mb-1">첫 구매 10% 할인</div>
                        <div className="text-xs text-gray-500 mb-4">지금 가입하고 즉시 쿠폰 받으세요</div>
                        <button className="w-full py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-bold shadow-lg shadow-emerald-500/30">
                          쿠폰 받기
                        </button>
                        <div className="mt-2 text-[10px] text-gray-400">다시 보지 않기</div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Stat Tooltip */}
                  <div className="absolute bottom-5 right-5 bg-gray-900/90 backdrop-blur border border-emerald-500/30 text-white px-4 py-2 rounded-lg shadow-xl z-20 animate-bounce-slow">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                      <span className="text-[10px] font-bold text-emerald-400 uppercase">실시간 전환</span>
                    </div>
                    <div className="flex items-end gap-3">
                      <div>
                        <div className="text-[9px] text-gray-400">클릭률</div>
                        <div className="text-base font-bold">18.5%</div>
                      </div>
                      <div className="w-px h-6 bg-gray-700"></div>
                      <div>
                        <div className="text-[9px] text-gray-400">전환</div>
                        <div className="text-base font-bold text-emerald-400">340</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-10 left-10 right-10 h-20 bg-emerald-500/20 blur-[60px] rounded-full transform scale-y-50"></div>
              </div>
            </div>

          </div>
        </div>

        <style>{`
          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        `}</style>
      </section>

      {/* ==========================================
          섹션 2: Why 팝업 CRO? — 아무 팝업 vs 데이터 기반 팝업
      ========================================== */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Why Popup CRO?
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              팝업은 <span className="text-emerald-600">'어떻게'</span> 띄우느냐가 <br className="sm:hidden" />전부입니다
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              무분별한 팝업은 방문자를 쫓아냅니다. 데이터 기반 팝업은 이탈하려던 고객을{' '}
              <br className="hidden md:block" />
              전환시킵니다.
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full items-center justify-center shadow-lg z-10 font-black text-gray-300 italic text-xl border-4 border-gray-50">
              VS
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-0">

              {/* 아무 팝업 */}
              <div className="bg-white p-8 md:p-12 rounded-2xl md:rounded-r-none md:rounded-l-2xl border border-gray-200 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-100 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 text-gray-400">
                    <XCircle className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-500 mb-2">무분별한 팝업</h3>
                  <p className="text-gray-400 text-sm mb-8 font-medium">Annoying &amp; Blind</p>

                  <ul className="space-y-6">
                    {[
                      { text: "모든 방문자에게 매번 노출", sub: "피로도 누적으로 이탈 유발" },
                      { text: "노출·클릭·전환을 알 수 없음", sub: "효과 측정 불가" },
                      { text: "개발자에게 요청하고 대기", sub: "빠른 실험 불가능" }
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-4 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="mt-1 min-w-[24px]">
                          <HelpCircle className="w-6 h-6 text-gray-300" />
                        </div>
                        <div>
                          <div className="text-gray-600 font-medium text-lg break-keep">"{item.text}"</div>
                          <div className="text-gray-400 text-xs mt-0.5">{item.sub}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* 데이터 기반 팝업 */}
              <div className="bg-[#1A1F2E] p-8 md:p-12 rounded-2xl md:rounded-l-none md:rounded-r-2xl border border-gray-700 shadow-2xl relative overflow-hidden transform md:scale-105 md:-my-4 z-0 ring-1 ring-emerald-500/20">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full blur-3xl opacity-60 -mr-20 -mt-20 animate-pulse"></div>
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg shadow-emerald-500/30">
                    <Megaphone className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">데이터 기반 팝업 CRO</h3>
                  <p className="text-emerald-400 text-sm mb-8 font-bold tracking-wide">Targeted &amp; Measured</p>

                  <ul className="space-y-6">
                    {[
                      { text: "적절한 대상에게 적절한 빈도로", sub: "세션·일간·전체 노출 캡" },
                      { text: "노출·클릭·전환 실시간 리포트", sub: "성과가 숫자로 증명됨" },
                      { text: "코드 없이 A/B 테스트까지", sub: "마케터가 직접 최적화" }
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-4">
                        <div className="mt-1 min-w-[24px]">
                          <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                            <Check className="w-4 h-4 text-emerald-400 stroke-[3]" />
                          </div>
                        </div>
                        <div>
                          <div className="text-white font-bold text-lg">{item.text}</div>
                          <div className="text-gray-400 text-xs mt-0.5 font-medium">{item.sub}</div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 pt-8 border-t border-gray-700">
                    <div className="flex items-center gap-2 text-emerald-400 font-bold">
                      <span>피로도는 줄이고, 전환은 높이고</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ==========================================
          섹션 3: 4가지 팝업 유형
      ========================================== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              목적에 맞는 4가지 팝업 형태
            </h2>
            <p className="text-xl text-gray-600">
              전환, 구독, 공지, 프로모션까지 <br className="sm:hidden" />상황에 맞는 형태를 선택하세요
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">

            {/* 1. 모달 팝업 */}
            <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-emerald-200 transition-all duration-300">
              <div className="h-48 bg-gray-50 rounded-xl mb-6 relative overflow-hidden flex items-center justify-center border border-gray-100 group-hover:bg-emerald-50/30 transition-colors">
                {/* Mock 사이트 + 모달 */}
                <div className="absolute inset-0 p-4 opacity-40">
                  <div className="w-full h-4 bg-white rounded mb-2 shadow-sm"></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-16 bg-white rounded shadow-sm"></div>
                    <div className="h-16 bg-white rounded shadow-sm"></div>
                  </div>
                </div>
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative w-32 bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100 z-10 group-hover:scale-105 transition-transform">
                  <div className="h-10 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-lg">🎁</div>
                  <div className="p-3 space-y-2">
                    <div className="h-2 w-3/4 bg-gray-200 rounded mx-auto"></div>
                    <div className="h-6 bg-emerald-600 rounded"></div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                  <Layout className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">모달 팝업</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                화면 중앙에 오버레이로 띄워 시선을 강력하게 집중시킵니다. 쿠폰, 첫 구매 혜택, 리드 확보에 효과적입니다.
              </p>
              <ul className="space-y-2">
                {['강력한 주목도', '쿠폰 · 리드 수집 최적', '이탈 방지(Exit Intent) 활용'].map((feat, i) => (
                  <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500 mr-2" /> {feat}
                  </li>
                ))}
              </ul>
            </div>

            {/* 2. 띠배너 */}
            <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-teal-200 transition-all duration-300">
              <div className="h-48 bg-gray-50 rounded-xl mb-6 relative overflow-hidden flex flex-col border border-gray-100 group-hover:bg-teal-50/30 transition-colors">
                {/* 상단 고정 배너 */}
                <div className="h-8 bg-gradient-to-r from-teal-500 to-emerald-500 flex items-center justify-center text-white text-[9px] font-bold gap-1 shadow-md z-10">
                  <Sparkles className="w-2.5 h-2.5" /> 무료 배송 이벤트 진행 중
                </div>
                <div className="flex-1 p-4 opacity-40 space-y-2">
                  <div className="h-3 w-1/2 bg-white rounded shadow-sm"></div>
                  <div className="h-16 bg-white rounded shadow-sm"></div>
                  <div className="h-3 w-3/4 bg-white rounded shadow-sm"></div>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-teal-100 text-teal-600 rounded-lg">
                  <PanelTop className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">띠배너</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                상단이나 하단에 고정되어 콘텐츠를 가리지 않으면서도 항상 노출됩니다. 공지, 안내, 상시 프로모션에 적합합니다.
              </p>
              <ul className="space-y-2">
                {['콘텐츠 방해 최소화', '전 페이지 상시 노출', '공지 · 안내에 최적'].map((feat, i) => (
                  <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                    <CheckCircle2 className="w-3 h-3 text-teal-500 mr-2" /> {feat}
                  </li>
                ))}
              </ul>
            </div>

            {/* 3. 슬라이드 배너 */}
            <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-amber-200 transition-all duration-300">
              <div className="h-48 bg-gray-50 rounded-xl mb-6 relative overflow-hidden border border-gray-100 group-hover:bg-amber-50/30 transition-colors">
                {/* Mock 사이트 배경 */}
                <div className="absolute inset-0 p-4 opacity-40 space-y-2">
                  <div className="h-3 w-1/2 bg-white rounded shadow-sm"></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-16 bg-white rounded shadow-sm"></div>
                    <div className="h-16 bg-white rounded shadow-sm"></div>
                  </div>
                </div>
                {/* 우측 하단 슬라이드인 카드 */}
                <div className="absolute bottom-3 right-3 w-32 bg-white rounded-lg shadow-2xl border border-amber-100 p-2.5 z-10 translate-x-2 group-hover:translate-x-0 transition-transform duration-500">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className="w-5 h-5 rounded bg-amber-100 flex items-center justify-center text-[10px]">🔔</div>
                    <div className="h-1.5 w-12 bg-gray-200 rounded"></div>
                  </div>
                  <div className="h-1.5 w-full bg-gray-100 rounded mb-2"></div>
                  <div className="h-5 bg-amber-500 rounded"></div>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">슬라이드 배너</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                화면 모서리에서 부드럽게 슬라이드되어 나타납니다. 거부감 없이 추천 상품이나 구독을 자연스럽게 유도합니다.
              </p>
              <ul className="space-y-2">
                {['부드러운 등장', '낮은 거부감', '추천 · 구독 유도'].map((feat, i) => (
                  <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                    <CheckCircle2 className="w-3 h-3 text-amber-500 mr-2" /> {feat}
                  </li>
                ))}
              </ul>
            </div>

            {/* 4. 전면 팝업 */}
            <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-violet-200 transition-all duration-300">
              <div className="h-48 rounded-xl mb-6 relative overflow-hidden border border-gray-100">
                {/* 흐릿한 배경 */}
                <div className="absolute inset-0 bg-gray-50 p-4 opacity-30">
                  <div className="w-full h-4 bg-white rounded mb-2"></div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="h-12 bg-white rounded"></div>
                    <div className="h-12 bg-white rounded"></div>
                    <div className="h-12 bg-white rounded"></div>
                  </div>
                </div>
                {/* 전면 오버레이 */}
                <div className="absolute inset-0 bg-gradient-to-br from-violet-600 to-purple-700 flex flex-col items-center justify-center text-center p-4 group-hover:from-violet-500 group-hover:to-purple-600 transition-colors">
                  <button className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white">
                    <X className="w-3 h-3" />
                  </button>
                  <div className="text-3xl mb-2">🎉</div>
                  <div className="text-white text-sm font-bold mb-2 leading-tight">여름 시즌<br />최대 40% 할인</div>
                  <div className="px-4 py-1.5 bg-white text-violet-700 rounded-full text-[10px] font-bold shadow-lg">혜택 받기</div>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-violet-100 text-violet-600 rounded-lg">
                  <Maximize2 className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">전면 팝업</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                화면 전체를 덮어 가장 강력하게 노출합니다. 이벤트·랜딩 진입 시점에 임팩트를 극대화할 때 사용하세요.
              </p>
              <ul className="space-y-2">
                {['최대 임팩트', '이벤트 · 랜딩 진입', '시선 독점'].map((feat, i) => (
                  <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                    <CheckCircle2 className="w-3 h-3 text-violet-500 mr-2" /> {feat}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ==========================================
          섹션 4: 노코드 에디터 + 형태 전환 (인터랙티브)
      ========================================== */}
      <section className="py-24 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">

            {/* Left: 설명 */}
            <div className="flex-1 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-6">
                <Palette className="w-4 h-4" />
                <span>No-Code Editor</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                코딩 없이,<br />
                <span className="text-emerald-600">클릭 몇 번으로 완성</span>
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                개발자에게 요청하고 기다릴 필요가 없습니다.
                문구, 이미지, 색상, 버튼을 직접 편집하고 <br className="sm:hidden" />모달·띠배너·슬라이드·전면 형태를
                자유롭게 전환하며 실시간 미리보기로 확인하세요.
              </p>

              <div className="space-y-6 mb-8">
                {[
                  { title: "형태 자유 전환", desc: "같은 콘텐츠를 모달·띠배너·슬라이드·전면으로 즉시 전환합니다.", icon: <Layers className="w-5 h-5 text-emerald-600" /> },
                  { title: "텍스트 · 색상 즉시 수정", desc: "문구부터 버튼 색상까지 에디터 위에서 바로 편집합니다.", icon: <Type className="w-5 h-5 text-emerald-600" /> }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 group">
                    <div className="mt-1 w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1 group-hover:text-emerald-600 transition-colors">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-white rounded-xl border border-emerald-100 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">👉 오른쪽에서 형태를 직접 바꿔보세요</h4>
                <p className="text-sm text-gray-600">버튼을 눌러 팝업이 어떻게 달라지는지 확인할 수 있습니다.</p>
              </div>
            </div>

            {/* Right: 인터랙티브 미리보기 */}
            <div className="flex-1 w-full relative">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden relative">
                {/* Toggle Header */}
                <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex justify-between items-center">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider hidden sm:inline">미리보기</span>
                  <div className="flex bg-gray-800 p-1 rounded-lg gap-0.5">
                    {[
                      { key: 'modal', label: '모달' },
                      { key: 'bar', label: '띠배너' },
                      { key: 'slide', label: '슬라이드' },
                      { key: 'full', label: '전면' }
                    ].map((t) => (
                      <button
                        key={t.key}
                        onClick={() => setPopupType(t.key)}
                        className={`px-2 py-1 rounded text-[11px] font-bold transition-all ${popupType === t.key ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-400 hover:text-white'}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview Stage */}
                <div className="relative h-80 bg-gray-50 overflow-hidden">
                  {/* Mock 사이트 배경 */}
                  <div className="absolute inset-0 p-4 pointer-events-none">
                    <div className="h-10 bg-white border border-gray-100 rounded-lg mb-4 flex items-center px-3 gap-2 shadow-sm">
                      <div className="w-6 h-6 bg-emerald-600 rounded"></div>
                      <div className="w-20 h-2.5 bg-gray-200 rounded"></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-lg border border-gray-100 shadow-sm p-2 space-y-2">
                          <div className="w-full h-14 bg-gray-100 rounded"></div>
                          <div className="w-3/4 h-2 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 모달 */}
                  {popupType === 'modal' && (
                    <>
                      <div className="absolute inset-0 bg-black/40"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-56 bg-white rounded-2xl shadow-2xl overflow-hidden relative">
                          <button className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white">
                            <X className="w-3.5 h-3.5" />
                          </button>
                          <div className="h-20 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-4xl">🎁</div>
                          <div className="p-4 text-center">
                            <div className="text-base font-bold text-gray-900 mb-1">첫 구매 10% 할인</div>
                            <div className="text-[11px] text-gray-500 mb-3">지금 가입하고 쿠폰 받기</div>
                            <button className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold">쿠폰 받기</button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* 띠배너 */}
                  {popupType === 'bar' && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-5 py-3 flex items-center justify-between shadow-2xl">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        <span className="text-sm font-bold">지금 가입하면 무료 배송 + 10% 쿠폰</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-1.5 bg-white text-teal-700 rounded-lg text-xs font-bold">받기</button>
                        <X className="w-4 h-4 opacity-70" />
                      </div>
                    </div>
                  )}

                  {/* 슬라이드 배너 */}
                  {popupType === 'slide' && (
                    <div className="absolute bottom-4 right-4 w-64 bg-white rounded-xl shadow-2xl border border-amber-100 overflow-hidden">
                      <div className="flex items-start gap-3 p-4">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-xl flex-shrink-0">🔔</div>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-gray-900 mb-0.5">이 상품 어때요?</div>
                          <div className="text-[11px] text-gray-500 mb-2">지금 보기 좋은 추천 상품이 도착했어요</div>
                          <button className="px-3 py-1 bg-amber-500 text-white rounded-md text-xs font-bold">구경하기</button>
                        </div>
                        <button className="text-gray-300"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  )}

                  {/* 전면 팝업 */}
                  {popupType === 'full' && (
                    <div className="absolute inset-0 bg-gradient-to-br from-violet-600 to-purple-700 flex flex-col items-center justify-center text-center p-6">
                      <button className="absolute top-3 right-3 w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-white">
                        <X className="w-4 h-4" />
                      </button>
                      <div className="text-5xl mb-3">🎉</div>
                      <div className="text-white text-xl font-bold mb-1">여름 시즌 최대 40% 할인</div>
                      <div className="text-white/80 text-xs mb-5">회원가입하고 지금 바로 쿠폰 받으세요</div>
                      <button className="px-6 py-2.5 bg-white text-violet-700 rounded-lg text-sm font-bold shadow-lg">혜택 받기</button>
                      <div className="mt-3 text-white/60 text-[11px]">다음에 하기</div>
                    </div>
                  )}
                </div>

                {/* Footer 힌트 */}
                <div className="bg-white border-t border-gray-100 px-4 py-3 flex items-center justify-between text-xs text-gray-400">
                  <span className="font-medium">현재 형태</span>
                  <span className="font-bold text-emerald-600">{{ modal: '모달', bar: '띠배너', slide: '슬라이드 배너', full: '전면 팝업' }[popupType]}</span>
                </div>
              </div>

              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-emerald-100 rounded-full blur-3xl -z-10"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-teal-100 rounded-full blur-3xl -z-10"></div>
            </div>

          </div>
        </div>
      </section>

      {/* ==========================================
          섹션 5: 방문자 맞춤 팝업 & 노출 제어 — 인터랙티브
      ========================================== */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16">

            {/* Right: 설명 (반전) */}
            <div className="flex-1 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-6">
                <Users className="w-4 h-4" />
                <span>Audience Targeting</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                방문자에 따라<br />
                <span className="text-teal-600">다른 팝업</span>을 띄우세요
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                모두에게 같은 팝업을 보여줄 필요는 없습니다.
                기기, 유입 경로, 방문 이력 등 다양한 조건으로 노출 대상을 정하세요 —
                예를 들어 신규 방문자에게는 첫 구매 혜택을, 재방문자에게는 재구매 혜택을.
                노출 빈도까지 제어할 수 있습니다.
              </p>

              <div className="space-y-6 mb-8">
                {[
                  { title: "방문자별 맞춤 팝업", desc: "신규엔 A팝업, 재방문엔 B팝업처럼 세그먼트마다 다른 팝업을 노출합니다.", icon: <Users className="w-5 h-5 text-teal-600" /> },
                  { title: "정밀 타겟팅 조건", desc: "기기, 유입 경로, 방문 이력 등으로 노출 대상을 정합니다.", icon: <Target className="w-5 h-5 text-teal-600" /> },
                  { title: "노출 빈도 제어", desc: "세션·일간·전체 캡으로 노출 피로도를 관리합니다.", icon: <SlidersHorizontal className="w-5 h-5 text-teal-600" /> }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1 w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-teal-50/50 rounded-xl border border-teal-100 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">👉 팝업마다 노출 대상을 설정합니다</h4>
                <p className="text-sm text-gray-600">신규는 A팝업, 재방문은 B팝업으로 — 팝업별로 타겟만 정하면 방문자마다 맞는 팝업이 노출됩니다.</p>
              </div>
            </div>

            {/* Left: 방문자 분기 라우팅 다이어그램 */}
            <div className="flex-1 w-full">
              <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 p-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:24px_24px] opacity-20"></div>

                <div className="relative z-10">
                  {/* 헤더 */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2 text-sm">
                      <Split className="w-4 h-4 text-teal-400" />
                      <span className="text-slate-300 font-medium">방문자 분기 라우팅</span>
                    </div>
                    <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-xs font-bold border border-teal-500/30">
                      규칙 기반 자동 노출
                    </span>
                  </div>

                  {/* 상단 소스 노드 */}
                  <div className="flex justify-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 shadow-lg">
                      <Users className="w-4 h-4 text-teal-400" />
                      <span className="text-sm font-bold text-white">방문자 유입</span>
                    </div>
                  </div>

                  {/* 분기 커넥터 (SVG) */}
                  <svg viewBox="0 0 320 68" className="w-full" style={{ height: '64px' }}>
                    <path d="M160,2 C160,34 80,34 80,66" stroke="#10b981" strokeWidth="2" strokeDasharray="5 4" fill="none" opacity="0.5" />
                    <path d="M160,2 C160,34 240,34 240,66" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5 4" fill="none" opacity="0.5" />
                    <circle r="4" fill="#10b981">
                      <animateMotion dur="2.2s" repeatCount="indefinite" path="M160,2 C160,34 80,34 80,66" />
                      <animate attributeName="opacity" values="0;1;1;0" dur="2.2s" repeatCount="indefinite" />
                    </circle>
                    <circle r="4" fill="#3b82f6">
                      <animateMotion dur="2.2s" begin="1.1s" repeatCount="indefinite" path="M160,2 C160,34 240,34 240,66" />
                      <animate attributeName="opacity" values="0;1;1;0" dur="2.2s" begin="1.1s" repeatCount="indefinite" />
                    </circle>
                  </svg>

                  {/* 두 분기 */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* 신규 → A팝업 */}
                    <div className="flex flex-col items-center">
                      <div className="mb-3 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[11px] font-bold">신규 방문자</div>
                      <div className="w-full max-w-[150px] bg-white rounded-xl shadow-2xl overflow-hidden">
                        <div className="h-10 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-xl">🎉</div>
                        <div className="p-2.5 text-center">
                          <div className="text-[11px] font-bold text-gray-900 mb-0.5">첫 방문 환영!</div>
                          <div className="text-[9px] text-gray-500 mb-2">10% 할인 쿠폰</div>
                          <div className="py-1 bg-emerald-600 rounded text-[9px] text-white font-bold">쿠폰 받기</div>
                        </div>
                      </div>
                      <div className="mt-2 text-[10px] text-emerald-400/70 font-mono font-bold">A팝업</div>
                    </div>

                    {/* 재방문 → B팝업 */}
                    <div className="flex flex-col items-center">
                      <div className="mb-3 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-[11px] font-bold">재방문자</div>
                      <div className="w-full max-w-[150px] bg-white rounded-xl shadow-2xl overflow-hidden">
                        <div className="h-10 bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-xl">👋</div>
                        <div className="p-2.5 text-center">
                          <div className="text-[11px] font-bold text-gray-900 mb-0.5">다시 오셨네요!</div>
                          <div className="text-[9px] text-gray-500 mb-2">재구매 15% 혜택</div>
                          <div className="py-1 bg-blue-600 rounded text-[9px] text-white font-bold">혜택 받기</div>
                        </div>
                      </div>
                      <div className="mt-2 text-[10px] text-blue-400/70 font-mono font-bold">B팝업</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ==========================================
          섹션 6: A/B 테스트 + 홀드아웃 (두 개의 비교 실험)
      ========================================== */}
      <section className="py-24 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 섹션 헤더 */}
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-6">
              <Split className="w-4 h-4" />
              <span>A/B Test &amp; Holdout</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              감이 아니라 <span className="text-emerald-600">데이터로 이기는 팝업</span>
            </h2>
            <p className="text-lg text-gray-600">
              어떤 팝업이 더 잘 전환되는지, 그리고 팝업이 정말 효과가 있는지까지 두 가지 실험으로 증명하세요.
            </p>
          </div>

          {/* ① 팝업 A/B 테스트 (변형 비교) */}
          <div className="relative bg-white rounded-3xl border border-gray-200 shadow-xl p-6 sm:p-10 overflow-hidden mb-8">
            <div className="absolute top-0 left-0 w-80 h-80 bg-emerald-50 rounded-full blur-3xl -ml-20 -mt-20 pointer-events-none"></div>

            <div className="text-center mb-10 relative z-10">
              <div className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-4">
                <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-black">1</span>
                <span>팝업 A/B 테스트</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">어떤 팝업이 더 잘 전환될까요?</h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                같은 페이지에 <strong className="text-gray-900">서로 다른 팝업 버전</strong>을 노출해 전환율이 높은 승자를 데이터로 가려냅니다.
              </p>
            </div>

            <div className="relative grid md:grid-cols-2 gap-6 lg:gap-16 items-start z-10">

              {/* VS 배지 (중앙) */}
              <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 bg-white rounded-full items-center justify-center shadow-lg z-20 font-black text-gray-300 italic text-lg border-4 border-gray-50">
                VS
              </div>

              {/* 버전 A */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50/50 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-400"></span>
                  <span className="text-sm font-bold text-gray-500">버전 A · "회원가입 혜택"</span>
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white relative h-52">
                  <div className="bg-gray-100 border-b border-gray-200 px-3 py-2 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-300"></div>
                    <div className="w-2 h-2 rounded-full bg-yellow-300"></div>
                    <div className="w-2 h-2 rounded-full bg-green-300"></div>
                  </div>
                  <div className="p-4 opacity-40">
                    <div className="w-1/3 h-3 bg-gray-200 rounded mb-3"></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-14 bg-gray-100 rounded"></div>
                      <div className="h-14 bg-gray-100 rounded"></div>
                    </div>
                  </div>
                  {/* 팝업 A 오버레이 */}
                  <div className="absolute inset-0 top-9 bg-black/20 flex items-center justify-center">
                    <div className="w-32 bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-100">
                      <div className="h-8 bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center text-sm">🎁</div>
                      <div className="p-2 text-center">
                        <div className="text-[9px] font-bold text-gray-700 mb-1">회원가입 혜택</div>
                        <div className="h-3.5 bg-gray-400 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="text-xs text-gray-400 font-medium">구매 전환율</span>
                  <span className="text-3xl font-bold text-gray-400">4.4%</span>
                </div>
              </div>

              {/* 버전 B (승자) */}
              <div className="relative rounded-2xl border-2 border-emerald-200 bg-emerald-50/30 p-5">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-md z-10">WINNER</div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                  <span className="text-sm font-bold text-gray-900">버전 B · "지금 10% 할인"</span>
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white relative h-52">
                  <div className="bg-gray-100 border-b border-gray-200 px-3 py-2 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-300"></div>
                    <div className="w-2 h-2 rounded-full bg-yellow-300"></div>
                    <div className="w-2 h-2 rounded-full bg-green-300"></div>
                  </div>
                  <div className="p-4 opacity-40">
                    <div className="w-1/3 h-3 bg-gray-200 rounded mb-3"></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-14 bg-gray-100 rounded"></div>
                      <div className="h-14 bg-gray-100 rounded"></div>
                    </div>
                  </div>
                  {/* 팝업 B 오버레이 */}
                  <div className="absolute inset-0 top-9 bg-black/20 flex items-center justify-center">
                    <div className="w-32 bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-100 ring-2 ring-emerald-400">
                      <div className="h-8 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-sm">🎁</div>
                      <div className="p-2 text-center">
                        <div className="text-[9px] font-bold text-gray-800 mb-1">지금 10% 할인</div>
                        <div className="h-3.5 bg-emerald-600 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="text-xs text-gray-500 font-medium">구매 전환율</span>
                  <span className="text-3xl font-bold text-emerald-600">6.2%</span>
                </div>
              </div>
            </div>

            {/* 결과 배너 */}
            <div className="relative z-10 mt-8 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-center flex-wrap gap-x-3 gap-y-1 text-center shadow-lg shadow-emerald-500/20">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-white" />
                <span className="text-white font-medium">버전 B가 버전 A 대비 전환</span>
              </div>
              <span className="text-white text-2xl font-black">+41%</span>
              <span className="text-white/80 text-sm">· 통계적 신뢰도 98.7%</span>
            </div>
          </div>

          {/* ② 팝업 있는 버전 vs 없는 버전 (홀드아웃) */}
          <div className="relative bg-white rounded-3xl border border-gray-200 shadow-xl p-6 sm:p-10 overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-50 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>

            {/* 헤더 */}
            <div className="text-center mb-10 relative z-10">
              <div className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-4">
                <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-black">2</span>
                <span>팝업 있는 버전 vs 없는 버전</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">팝업, 정말 효과가 있을까요?</h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                같은 조건에서 팝업을 <strong className="text-gray-900">본 그룹</strong>과 <strong className="text-gray-900">보지 않은 대조군</strong>의 전환율을 직접 비교합니다.
              </p>
            </div>

            {/* 비교 카드 */}
            <div className="relative grid md:grid-cols-2 gap-6 lg:gap-16 items-start z-10">

              {/* VS 배지 (중앙) */}
              <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 bg-white rounded-full items-center justify-center shadow-lg z-20 font-black text-gray-300 italic text-lg border-4 border-gray-50">
                VS
              </div>

              {/* 팝업 노출 그룹 */}
              <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/30 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                  <span className="text-sm font-bold text-gray-900">팝업 노출 그룹</span>
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white relative h-52">
                  <div className="bg-gray-100 border-b border-gray-200 px-3 py-2 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-300"></div>
                    <div className="w-2 h-2 rounded-full bg-yellow-300"></div>
                    <div className="w-2 h-2 rounded-full bg-green-300"></div>
                  </div>
                  <div className="p-4 opacity-40">
                    <div className="w-1/3 h-3 bg-gray-200 rounded mb-3"></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-14 bg-gray-100 rounded"></div>
                      <div className="h-14 bg-gray-100 rounded"></div>
                    </div>
                  </div>
                  {/* 팝업 오버레이 */}
                  <div className="absolute inset-0 top-9 bg-black/20 flex items-center justify-center">
                    <div className="w-32 bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-100">
                      <div className="h-8 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-sm">🎁</div>
                      <div className="p-2 text-center">
                        <div className="text-[9px] font-bold text-gray-800 mb-1">지금 10% 할인</div>
                        <div className="h-3.5 bg-emerald-600 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="text-xs text-gray-500 font-medium">구매 전환율</span>
                  <span className="text-3xl font-bold text-emerald-600">6.2%</span>
                </div>
              </div>

              {/* 대조군 */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50/50 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-300"></span>
                  <span className="text-sm font-bold text-gray-500">대조군 · 팝업 없음</span>
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white relative h-52">
                  <div className="bg-gray-100 border-b border-gray-200 px-3 py-2 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                    <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                    <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                  </div>
                  <div className="p-4 opacity-40">
                    <div className="w-1/3 h-3 bg-gray-200 rounded mb-3"></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-14 bg-gray-100 rounded"></div>
                      <div className="h-14 bg-gray-100 rounded"></div>
                    </div>
                    <div className="w-2/3 h-3 bg-gray-100 rounded mt-3"></div>
                    <div className="w-1/2 h-3 bg-gray-100 rounded mt-2"></div>
                  </div>
                  <div className="absolute bottom-3 right-3 text-[10px] text-gray-300 font-medium border border-dashed border-gray-200 rounded px-2 py-0.5">팝업 없음</div>
                </div>
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="text-xs text-gray-400 font-medium">구매 전환율</span>
                  <span className="text-3xl font-bold text-gray-400">3.1%</span>
                </div>
              </div>
            </div>

            {/* 결과 업리프트 배너 */}
            <div className="relative z-10 mt-8 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-center flex-wrap gap-x-3 gap-y-1 text-center shadow-lg shadow-emerald-500/20">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-white" />
                <span className="text-white font-medium">팝업 노출 그룹의 전환율이 대조군 대비</span>
              </div>
              <span className="text-white text-2xl font-black">+100% (2배)</span>
            </div>
          </div>

        </div>
      </section>

      {/* ==========================================
          섹션 7: 활용 사례
      ========================================== */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4">
              <Search className="w-3 h-3" />
              <span>Use Cases</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              팝업 한 번으로 만들어낸 <br className="sm:hidden" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">전환의 순간들</span>
            </h2>
            <p className="text-xl text-slate-400">
              적절한 타이밍의 팝업이 <br className="sm:hidden" />비즈니스 지표를 어떻게 바꾸는지 확인하세요.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {useCases.map((item, index) => (
              <div
                key={index}
                className="group relative bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:bg-slate-800 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-${item.color}-500/10 rounded-full blur-3xl -mr-10 -mt-10 group-hover:opacity-100 transition-opacity`}></div>

                <div className="flex justify-between items-start mb-6 relative z-10">
                  <div className={`p-3 rounded-xl bg-slate-900 border border-slate-700 text-${item.color}-400 group-hover:text-white group-hover:bg-${item.color}-600 transition-colors shadow-lg ${item.glow}`}>
                    {item.emoji}
                  </div>
                  <span className={`px-3 py-1 rounded-full bg-${item.color}-900/30 text-${item.color}-400 text-xs font-bold border border-${item.color}-500/20`}>
                    {item.type}
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-white mb-6 relative z-10">
                  {item.title}
                </h3>

                <div className="space-y-6 mb-8 relative z-10">
                  <div className="flex gap-4">
                    <div className="mt-1 min-w-[24px]">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-red-400 uppercase tracking-wide block mb-1">발견된 문제</span>
                      <p className="text-sm text-slate-300 leading-relaxed">{item.problem}</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="mt-1 min-w-[24px]">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-emerald-400 uppercase tracking-wide block mb-1">팝업 솔루션</span>
                      <p className="text-sm text-slate-300 leading-relaxed">{item.solution}</p>
                    </div>
                  </div>
                </div>

                <div className="relative z-10 bg-slate-900 rounded-xl p-4 border border-slate-700 flex items-center justify-between group-hover:border-slate-600 transition-colors shadow-inner">
                  <div>
                    <div className="text-xs text-slate-500 mb-1 font-medium uppercase">Impact</div>
                    <div className={`text-xl font-bold text-${item.color}-400`}>{item.result}</div>
                  </div>
                  <div className={`w-10 h-10 rounded-full bg-${item.color}-500/10 flex items-center justify-center text-${item.color}-400`}>
                    <ArrowUpRight className="w-5 h-5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==========================================
          섹션 8: 주요 특징 벤토 그리드
      ========================================== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              가볍지만 강력한 캠페인 엔진
            </h2>
            <p className="text-xl text-gray-600">
              사이트 성능 저하 없이, 원하는 팝업을<br className="sm:hidden" /> 원하는 순간에 노출합니다.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* 1. 노코드 에디터 (Large) */}
            <div className="md:col-span-2 bg-gray-50 rounded-2xl p-8 border border-gray-200 relative overflow-hidden group hover:border-emerald-200 transition-colors">
              <div className="absolute top-6 right-6 flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-gray-200 shadow-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-bold text-gray-600">No-Code</span>
              </div>

              <div className="relative z-10">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-6 text-emerald-600">
                  <Palette className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">노코드 팝업 에디터</h3>
                <p className="text-gray-600 leading-relaxed max-w-md">
                  문구, 이미지, 색상, 버튼을 마케터가 직접 편집합니다. <br className="hidden sm:block" />
                  모달·띠배너·슬라이드·전면을 자유롭게 전환하고 <strong>실시간 미리보기</strong>로
                  결과를 즉시 확인하세요.
                </p>
              </div>

              <div className="absolute right-0 bottom-0 opacity-10">
                <svg width="200" height="100" viewBox="0 0 200 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 80 C 50 80, 50 20, 100 20 C 150 20, 150 50, 200 50" stroke="currentColor" strokeWidth="4" className="text-emerald-500" />
                </svg>
              </div>
            </div>

            {/* 2. 노출 제어 (Dark Tall) */}
            <div className="bg-gray-900 text-white rounded-2xl p-8 border border-gray-800 relative overflow-hidden group">
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                    <Gauge className="w-6 h-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">정교한 노출 제어</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    세션 · 일간 · 전체 기준으로<br />
                    노출 횟수를 세밀하게 제어.<br />
                    <strong>방문자 피로도</strong>를 관리해<br />
                    이탈 없이 전환만 남깁니다.
                  </p>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <div className="flex justify-between items-center text-sm font-mono text-gray-400">
                    <span>Frequency Cap</span>
                    <span className="text-emerald-400 font-bold">Smart</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. 초경량 SDK */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
              <div className="flex justify-between items-start mb-6">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center text-teal-600">
                  <Zap className="w-6 h-6" />
                </div>
                <div className="text-xs font-mono text-gray-400 bg-gray-50 px-2 py-1 rounded">~5kb</div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">초경량 통합 SDK</h3>
              <p className="text-gray-600 text-sm">
                기존 A/B 테스트 SDK에 통합된 팝업 엔진.<br />
                추가 스크립트 설치 없이 즉시 사용할 수 있습니다.
              </p>
            </div>

            {/* 4. 실시간 성과 + 프라이버시 (Wide) */}
            <div className="md:col-span-2 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100 flex flex-col sm:flex-row items-center gap-6">
              <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-emerald-500">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-bold text-gray-900 mb-2">실시간 성과 추적 &amp; 중복 제거</h3>
                <p className="text-gray-600 text-sm">
                  노출·클릭·전환을 실시간으로 집계하며, 짧은 시간 내 반복 전환은 자동으로 제거해<br className="hidden sm:block" />
                  정확한 성과만 리포트합니다. 데이터 소유권은 100% 고객사에 있습니다.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ==========================================
          섹션 9: 시너지 (팝업 = 실행/개입 레이어)
      ========================================== */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-emerald-100/50 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/3"></div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-teal-100/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">

            {/* Left: 텍스트 */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-sm font-bold mb-6">
                <Zap className="w-4 h-4 fill-emerald-600" />
                <span>Action Layer</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                발견은 다른 도구가,<br />
                <span className="text-emerald-600">실행은 팝업</span>이 합니다
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                퍼널·히트맵·세션 녹화로 '어디서·왜' 이탈하는지 찾았다면,
                팝업으로 바로 '지금 이 순간' 개입하세요.
                발견 → 원인 확인 → 실시간 개입 → 검증까지 한 플랫폼에서 끊김 없이 이어집니다.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4 bg-purple-50/50 p-4 rounded-xl border border-purple-100">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">1</div>
                  <div>
                    <div className="font-bold text-gray-900">이탈 지점 발견 (Funnel · Heatmap · Replay)</div>
                    <div className="text-sm text-gray-500">"장바구니에서 62% 이탈" → 지점 파악</div>
                  </div>
                </div>

                <div className="pl-7"><ArrowDown className="w-5 h-5 text-gray-300" /></div>

                <div className="flex items-center gap-4 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold">2</div>
                  <div>
                    <div className="font-bold text-gray-900">즉시 개입 (Popup)</div>
                    <div className="text-sm text-gray-500">"이탈 시점에 쿠폰 팝업 노출" → 전환 유도</div>
                  </div>
                </div>

                <div className="pl-7"><ArrowDown className="w-5 h-5 text-gray-300" /></div>

                <div className="flex items-center gap-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">3</div>
                  <div>
                    <div className="font-bold text-gray-900">팝업 최적화 (A/B Test)</div>
                    <div className="text-sm text-gray-500">"문구·혜택 A/B 비교" → 승자 적용</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/service/funnel">
                  <button className="flex items-center gap-2 text-purple-600 font-bold hover:text-purple-700 transition-colors group">
                    퍼널 분석 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                <Link to="/service/heatmap">
                  <button className="flex items-center gap-2 text-orange-600 font-bold hover:text-orange-700 transition-colors group">
                    히트맵 분석 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                <Link to="/service/ab-test">
                  <button className="flex items-center gap-2 text-blue-600 font-bold hover:text-blue-700 transition-colors group">
                    A/B 테스트 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                <Link to="/service/replay">
                  <button className="flex items-center gap-2 text-cyan-600 font-bold hover:text-cyan-700 transition-colors group">
                    세션 녹화 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </div>
            </div>

            {/* Right: 시각화 */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-3xl transform rotate-3 scale-105 opacity-60 blur-xl"></div>

              <div className="relative bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-xl flex flex-col items-center justify-center h-full min-h-[480px]">
                <div className="flex flex-col items-center gap-6 w-full mb-8">
                  <div className="text-center group">
                    <div className="w-24 h-24 bg-purple-50 border border-purple-200 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                      <Filter className="w-12 h-12 text-purple-500 fill-purple-500" />
                    </div>
                    <div className="font-bold text-gray-900 text-lg">이탈 지점 발견</div>
                    <div className="text-xs text-gray-500 mt-1 font-medium">퍼널 · 히트맵 · 세션 녹화</div>
                  </div>

                  <ArrowDown className="w-6 h-6 text-gray-300" />

                  <div className="text-center group">
                    <div className="w-24 h-24 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                      <Megaphone className="w-12 h-12 text-emerald-500 fill-emerald-500" />
                    </div>
                    <div className="font-bold text-gray-900 text-lg">즉시 개입</div>
                    <div className="text-xs text-gray-500 mt-1 font-medium">팝업 · 배너</div>
                  </div>

                  <ArrowDown className="w-6 h-6 text-gray-300" />

                  <div className="text-center group">
                    <div className="w-24 h-24 bg-blue-50 border border-blue-200 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                      <Split className="w-12 h-12 text-blue-500" />
                    </div>
                    <div className="font-bold text-gray-900 text-lg">최적화 검증</div>
                    <div className="text-xs text-gray-500 mt-1 font-medium">A/B 테스트</div>
                  </div>
                </div>

                <div className="bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg animate-bounce flex items-center gap-2 z-10">
                  <span>🚀</span>
                  <span>전환율 극대화 달성!</span>
                </div>
              </div>
            </div>
          </div>

          {/* 하단 연동 카드 */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-purple-50 to-violet-50/30 p-8 rounded-2xl border border-purple-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-purple-600 shadow-sm group-hover:scale-110 transition-transform">
                <Target className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">이탈 지점 타겟 팝업</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                퍼널·히트맵·세션 녹화로 확인한 이탈 지점에 정확히 팝업을 배치하세요.
                고객이 떠나려는 바로 그 순간, 혜택으로 붙잡을 수 있습니다.
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50/30 p-8 rounded-2xl border border-emerald-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-emerald-600 shadow-sm group-hover:scale-110 transition-transform">
                <Split className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">팝업 A/B 테스트 연동</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                팝업 문구와 혜택을 A/B로 비교해 가장 잘 전환되는 버전을 찾으세요.
                감이 아닌 데이터로 팝업 성과를 지속적으로 끌어올립니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================
          섹션 10: CTA Section
      ========================================== */}
      <section className="relative py-32 overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-900/40 via-slate-900 to-slate-900"></div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-emerald-600/20 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute top-20 left-20 w-1 h-1 bg-white rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-40 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse delay-700"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm font-medium mb-8 animate-fade-in-up">
            <Megaphone className="w-4 h-4 fill-emerald-500" />
            <span>Convert the moment</span>
          </div>

          <h2 className="text-3xl md:text-6xl font-bold text-white mb-8 tracking-tight leading-tight">
            떠나려는 고객을 <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">지금 붙잡으세요.</span>
          </h2>

          <p className="text-lg md:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
            적절한 순간의 팝업 하나가 전환율을 바꿉니다.<br className="hidden md:block" />
            코드 없이 만들고, 빈도까지 제어하는 <br className="sm:hidden" />팝업 CRO를 시작하세요.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/freestart" className="w-full sm:w-auto">
              <button className="w-full px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white rounded-xl font-bold text-lg shadow-[0_0_40px_-10px_rgba(16,185,129,0.3)] hover:shadow-[0_0_60px_-15px_rgba(16,185,129,0.5)] hover:-translate-y-1 transition-all flex items-center justify-center gap-2 group">
                무료로 시작하기
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <button className="w-full px-8 py-4 bg-transparent border border-slate-700 text-slate-300 rounded-xl font-medium hover:bg-slate-800 hover:text-white transition-all">
                도입 문의하기
              </button>
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-slate-500 font-medium">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" /> 30일 무료 체험
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" /> 신용카드 불필요
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" /> 언제든 취소 가능
            </div>
          </div>
        </div>
      </section>

      {/* <SimpleKakaoButton
        channelId="_yixgxjn"
        buttonText="카카오로 문의하기"
        mobileText="카카오 문의"
      /> */}

      <Footer />
    </div>
  );
};

export default ServicePopup;